import classnames from 'classnames';
import { motion } from 'framer-motion';
import React, { useMemo } from 'react';

import {
	ActionMenu,
	ActionMenuItem,
	ActionMenuTrigger,
	Checkbox,
	MoreButton,
	Text,
} from '@ekhodealer/ekho-common/rev';
import { parseVideoUrl } from '@ekhodealer/ekho-common/utils';

import type { MediaItem } from './MediaModal';

import Bin24 from '@common/components/icons/Bin24';
import Eye24 from '@common/components/icons/Eye24';

import './MediaGridItem.scss';

const GripIcon = (): React.JSX.Element => (
	<svg width='10' height='14' viewBox='0 0 10 14' fill='none' aria-hidden='true'>
		<circle cx='3' cy='2' r='1.5' fill='currentColor' />
		<circle cx='7' cy='2' r='1.5' fill='currentColor' />
		<circle cx='3' cy='7' r='1.5' fill='currentColor' />
		<circle cx='7' cy='7' r='1.5' fill='currentColor' />
		<circle cx='3' cy='12' r='1.5' fill='currentColor' />
		<circle cx='7' cy='12' r='1.5' fill='currentColor' />
	</svg>
);

export interface MediaGridItemProps {
	/** The media item data to render */
	item: MediaItem;
	/** 1-based order number displayed as a badge overlay */
	index: number;
	/** Whether this item is the one currently being dragged */
	isDragging: boolean;
	/** Called when the item is clicked (opens full-size view) */
	onClick: () => void;
	/** Called to delete this item */
	onDelete: () => void;
	/** Called with the item's id when drag starts from the handle */
	onDragStart: (id: string) => void;
	/** Called with the target item's id when a dragged item enters this item */
	onDragOver: (id: string) => void;
	/** Called when a drag operation ends */
	onDragEnd: () => void;
	/** Whether select mode is active */
	isSelectMode: boolean;
	/** Whether this item is currently selected */
	isSelected: boolean;
	/** Called when the item's selection state should toggle */
	onSelect: () => void;
}

export const MediaGridItem = ({
	item,
	index,
	isDragging,
	onClick,
	onDelete,
	onDragStart,
	onDragOver,
	onDragEnd,
	isSelectMode,
	isSelected,
	onSelect,
}: MediaGridItemProps): React.JSX.Element => {
	const videoThumbnailUrl = useMemo((): string | null => {
		if (item.type !== 'video') return null;
		const parsed = parseVideoUrl(item.url);
		if (parsed?.vendor === 'youtube') return parsed.thumbnailUrl;
		return null;
	}, [item.type, item.url]);

	const handleHandleDragStart = (e: React.DragEvent<HTMLDivElement>): void => {
		e.dataTransfer.effectAllowed = 'move';
		// Suppress the browser's native drag ghost — items slide to their destination
		// in real time, so a ghost following the cursor would be redundant noise.
		const ghost = document.createElement('div');
		ghost.style.cssText =
			'position:fixed;top:-9999px;left:-9999px;width:1px;height:1px;opacity:0;pointer-events:none;';
		document.body.appendChild(ghost);
		e.dataTransfer.setDragImage(ghost, 0, 0);
		requestAnimationFrame(() => document.body.removeChild(ghost));
		onDragStart(item.id);
	};

	const handleCardDragOver = (e: React.DragEvent<HTMLDivElement>): void => {
		// Prevent default to mark this as a valid drop target (shows move cursor).
		e.preventDefault();
		e.dataTransfer.dropEffect = 'move';
	};

	const handleCardDragEnter = (e: React.DragEvent<HTMLDivElement>): void => {
		// dragenter bubbles from children — only fire when genuinely entering this
		// card from outside (relatedTarget is not a descendant of this card).
		if ((e.currentTarget as HTMLElement).contains(e.relatedTarget as Node | null)) return;
		onDragOver(item.id);
	};

	const handleHandleDragEnd = (): void => {
		onDragEnd();
	};

	return (
		<motion.div
			layout
			transition={{ type: 'spring', stiffness: 300, damping: 30 }}
			className={classnames('media-grid-item', {
				'media-grid-item--dragging': isDragging,
				'media-grid-item--selected': isSelected,
			})}
			onDragOver={handleCardDragOver}
			onDragEnter={handleCardDragEnter}
			onClick={isSelectMode ? onSelect : onClick}
			role='button'
			tabIndex={0}
			onKeyDown={(e) => {
				if (e.key === 'Enter' || e.key === ' ') {
					e.preventDefault();
					if (isSelectMode) {
						onSelect();
					} else {
						onClick();
					}
				}
			}}
			aria-label={`Media item ${index}`}>
			<img
				className='media-grid-item__media'
				src={videoThumbnailUrl ?? item.url}
				alt={item.type === 'video' ? `Video ${index}` : `Media ${index}`}
			/>

			{item.type === 'video' && (
				<div className='media-grid-item__play-overlay' aria-hidden='true'>
					<svg width='36' height='36' viewBox='0 0 36 36' fill='none'>
						<circle cx='18' cy='18' r='18' fill='white' fillOpacity='0.9' />
						<path d='M14 11.5L26 18L14 24.5V11.5Z' fill='black' />
					</svg>
				</div>
			)}

			{/* Dark overlay — sits above the image but below all badges; shown on hover/menu-open */}
			<div className='media-grid-item__overlay' aria-hidden='true' />

			{/* Number badge — top-left */}
			<div className='media-grid-item__order-badge'>
				<Text size='footnote' weight='semibold'>
					{index}
				</Text>
			</div>

			{/* Duration badge — bottom-left (videos only) */}
			{item.type === 'video' && item.durationLabel && (
				<div className='media-grid-item__duration-badge'>
					<Text size='footnote' weight='regular'>
						{item.durationLabel}
					</Text>
				</div>
			)}

			{/* Select mode checkbox — top-right (replaces three-dot menu) */}
			{isSelectMode ? (
				<div
					className='media-grid-item__select-overlay'
					onClick={(e) => {
						e.stopPropagation();
						onSelect();
					}}>
					<Checkbox
						isSelected={isSelected}
						onChange={onSelect}
						ariaLabel={`Select media item ${index}`}
					/>
				</div>
			) : (
				/* Three-dot menu — top-right */
				<div
					className='media-grid-item__menu'
					onClick={(e) => {
						e.stopPropagation();
					}}>
					<ActionMenuTrigger>
						<MoreButton size='medium' aria-label={`Options for media item ${index}`} />
						<ActionMenu>
							<ActionMenuItem
								label='View full size'
								leadingAccessory={<Eye24 />}
								onAction={onClick}
							/>
							<ActionMenuItem
								label='Delete'
								leadingAccessory={<Bin24 />}
								onAction={onDelete}
								destructive
							/>
						</ActionMenu>
					</ActionMenuTrigger>
				</div>
			)}

			{/* Drag handle — bottom-right, drag starts here only */}
			{!isSelectMode && (
				<div
					className='media-grid-item__handle-badge'
					draggable
					onDragStart={handleHandleDragStart}
					onDragEnd={handleHandleDragEnd}
					onClick={(e) => {
						e.stopPropagation();
					}}
					aria-label='Drag to reorder'>
					<GripIcon />
				</div>
			)}
		</motion.div>
	);
};
